import React, { useState, useEffect } from 'react';
import { BCMSCurrency, BCMSDate, useGlobalController, Workflow, BCMSRecord, useWorkflowStep, useBCMSModal } from "bcms-react";
import { Account, Bill, Transaction, PaymentTransaction } from 'model';

import "./styles.css";
import "./mobile.css";
import { SVGAsset } from 'res';
import CustomModal, { CustomModalContent, CustomModalFooter, ModalCloseButton, ModalPrimaryButton, ModalSecondaryButton, useCustomModal } from 'components/custom-ui/CustomModal';
import { FormContainer, FormRowView, FormRow } from 'components/shared';
import CustomSelect, { CustomSelectItem } from 'components/custom-ui/CustomSelect';
import { UIAmountInput } from 'components/ui';
import DateSelect from 'components/custom-ui/DateSelect';
import { CustomAccountSelect } from 'components/custom-ui/CustomSelect';

const steps = [ <MakeAPaymentContent />, <VerifyPaymentContent /> ];

/**
 * @function MakeAPaymentModal
 * @return {React.Component}
 * @exported true
 * @description The main component for the MakeAPaymentModal
 */
export default function MakeAPaymentModal(props) {
  // Initalize the globalController and create a state for the modal title
  const controller = useGlobalController();
  // Get the default state and add the title state objects
  const { success, id } = props.data ?? {};
  const value = getDefaultPayment(controller, id, success);



  return (
    <CustomModal title="Make a Payment" id="MakeAPaymentModal" className="make-a-payment-modal">
        {/* When showing the VerifyPaymentContent, title of the modal needs to change to Verify Payment */}
        <Workflow value={ value } steps={ steps } />
    </CustomModal>
  );
}

const getWorkflowSteps = () => steps;

function getDefaultPayment(controller, id, success) {
  // Initalize the globalControllers for the classes required in the workflow
  BCMSRecord.globalControllerFor([ Account, Bill, Transaction, PaymentTransaction ], controller);
  // Initalize a new 'transaction' and 'paymentTransaction'
  const transaction = Transaction.init({});
  const bill = id !== undefined ? Bill.initByID(id) : Bill.init({});
  // Set transaction data to make a pending payment
  transaction.status = "pending";
  transaction.type = "regular";
  transaction.description = "Payment";
  transaction.id = Transaction.nextID();
  transaction.date = BCMSDate.init().nextBusinessDay();
  transaction.account = Account.firstInstanceOf();

  const option = id !== undefined
    ? `${ bill.due.format("m/d/Y") } eBill of ${ bill.amount.format() }`
    : "";

  // Return the default data for the workflow
  return { 
    transaction, 
    bill,
    success,

    "applyPaymentTo" : option,
    "paymentOption" : ""
  };
}

/**
 * @function MakeApaymentContent
 * @return {React.Component}
 * @exported false
 * @description This is the modal content for Make a Payment - the first step in the workflow in the MakeAPaymentModal.
 */
function MakeAPaymentContent(props) {
  const customModal = useCustomModal();
  // Update the title of the modal
  // useEffect is needed to prevent an error from being thrown about multiple state updates
  useEffect(() => customModal.setTitleState("Make a Payment"));
  // Initialize the workflowStep object and create all CustomSelectItem(s) for all bills in model
  const workflowStep = useWorkflowStep(getDefaultPayment, getWorkflowSteps);
  const billItems = Bill.initAll().map(bill => <CustomSelectItem key={ `Bill_${ bill.id }` } value={ bill }>{ bill.name }</CustomSelectItem>, []);
  // Set the only accounts able to transfer from either a checking or savings account
  const accountItems = Account.initAll().filter(item => item.type === "checking" || item.type === "savings", []);

  // Read in the individual items from the workflowStep
  const { transaction, bill, paymentOption, applyPaymentTo } = workflowStep.getStepState;
  // Get the bill from the 'paymentTransaction' and set variables for the amount and date with placeholders
  const billAmount = (bill && bill.amount) ? bill.amount.format() : "[$X.XX]";
  const billDate = (bill && bill.due) ? bill.due.format("m/d/Y") : "[MM/DD/YYYY]";
  const transactionAmount = BCMSCurrency.toDecimal(transaction.amount.amount) === "NaN" 
  ? undefined 
  : BCMSCurrency.toDecimal(transaction.amount.amount);

  // Update functions to update the workflowStep state
  const updateTransaction = prop => value => workflowStep.update(["transaction", prop], value);
  const updateBill = value => workflowStep.update(["bill"], value);
  const updateGeneral = prop => value => workflowStep.update([prop], value);
  const updateAccount = value => workflowStep.update(["transaction", "account"], Account.initByID(value.id));
  const onAmountBlur = val => workflowStep.update(["transaction", "amount"], val);

  // Checks to show/hide content based on what is selected
  const isEBill = bill !== undefined ? bill.isEBill === "true" : false;
  const showAmount = !isEBill || (isEBill && (paymentOption === "Other Amount" || applyPaymentTo === "None of these bills"));
  const showPaymentOption = isEBill && applyPaymentTo !== "None of these bills";

  // Values for the 'Payment Option' and 'Apply Payment To' selects
  const eBillOfOption = `${ billDate } eBill of ${ billAmount }`;
  const amountDueOption = `Amount Due ${ billAmount }`;

  // Submit function to go to the next step of the workflow
  const submit = evt => {
    const { accounts } = workflowStep.model;
    const { paymentOption } = workflowStep.getStepState;

    const amount = paymentOption !== undefined && paymentOption.indexOf("Amount Due") !== -1
      ? bill.amount.amount : transaction.amount.amount;

    // Establish default data for the workflow if nothing has been set
    // Setting these here since fields should be empty by default, and 
    // setting this data in the workflow as default would show on inital load
    workflowStep.update(["bill"], bill.id === undefined ? Bill.initAll()[0] : bill);
    workflowStep.update(["transaction", "account"], transaction.account ?? Account.initByID(accounts[0].id));
    workflowStep.update(["applyPaymentTo"], applyPaymentTo === "" ? "None of these bills" : applyPaymentTo);
    workflowStep.update(["transaction", "amount", "amount"], amount === undefined ? 100 : amount);
    // Route to the next step in the workflow
    workflowStep.workflow.nextStep(workflowStep.getStepState);
  };
  
  return (
    <>
      <CustomModalContent>
        <FormContainer>
          <FormRowView text="Pay From">
            <CustomAccountSelect value={ transaction.account } accounts={ accountItems } onChange={ updateAccount } />
          </FormRowView>
          <FormRowView text="Pay To">
            <CustomSelect value={ bill } onChange={ updateBill }>
              { billItems }
            </CustomSelect>
          </FormRowView>   
            
          {
            isEBill && 
            <>
              <FormRow formLabel={
                <div className="flex-container flex-horizontal align-top">
                  <span className="form-label">Apply Payment To</span>
                  <SVGAsset id="InfoIcon" className="inline-text-icon right-icon" />
                </div>
              }>
                <CustomSelect value={ applyPaymentTo } onChange={ updateGeneral("applyPaymentTo") }>
                  <CustomSelectItem value={ eBillOfOption }>{ eBillOfOption }</CustomSelectItem>
                  <CustomSelectItem value="None of these bills">None of these bills</CustomSelectItem>
                </CustomSelect>
              </FormRow>

              { showPaymentOption && 
                <FormRowView text="Payment Option">
                  <CustomSelect value={ paymentOption } onChange={ updateGeneral("paymentOption") }>
                    <CustomSelectItem value={ amountDueOption }>{ amountDueOption }</CustomSelectItem>
                    <CustomSelectItem value="Other Amount">Other Amount</CustomSelectItem>
                  </CustomSelect>
                </FormRowView> }
            </>
          }

          { showAmount &&
            <FormRowView text="Amount">
              <UIAmountInput value={ transactionAmount ?? "" } onBlur={ onAmountBlur } />
            </FormRowView> }

          <FormRowView text="Deliver By">
            <DateSelect value={ transaction.date } onChange={ updateTransaction("date") }/>
          </FormRowView>
        </FormContainer>
      </CustomModalContent>
      <CustomModalFooter>
        <ModalCloseButton id="MakeAPaymentModal" />
        <ModalPrimaryButton text="Continue" onClick={ submit } />
      </CustomModalFooter>
    </>
  );
}

/**
 * @function VerifyPaymentContent
 * @return {React.Component}
 * @exported false
 * @description This is the modal content Verify Payment - the second step in the MakeAPaymentModal workflow.
 */
function VerifyPaymentContent(props) {
  const customModal = useCustomModal();
  // Update the title of the modal
  // useEffect is needed to prevent an error from being thrown about multiple state updates
  useEffect(() => customModal.setTitleState("Verify Payment"));
  // Initalize the workflowStep and get the individual data from the workflowStep state
  const workflowStep = useWorkflowStep(getDefaultPayment, getWorkflowSteps);
  const { close } = useBCMSModal();
  const { transaction, bill, applyPaymentTo, paymentOption } = workflowStep.getStepState;

  // Set variables to the account and bill
  const account = Account.initByType(transaction.account.id);
  // Checks to show/hide content based on what was selected
  const isEBill = bill !== undefined ? bill.isEBill === "true" : false;
  const showAmount = !isEBill || (isEBill && (paymentOption === "Other Amount" || applyPaymentTo === "None of these bills"));
  const showPaymentOption = isEBill && applyPaymentTo !== "None of these bills";
  // Back button to return to the previous step in the workflow
  const backBtn = evt => workflowStep.workflow.prevStep(workflowStep.getStepState);
  const submit = evt => {
    workflowStep.update(["bill", "transaction"], transaction);
    // Update the transaction amount if its not defined
    if(transaction.amount.amount === undefined) {
      workflowStep.update(["transaction", "amount"], bill.amount);
    }
    // Create a new PaymentTransaction
    const payment = PaymentTransaction.init({ ...transaction.record, "billID" : bill });
    payment.mapID = transaction.id;
    // Update global model
    BCMSRecord.batch([
      Transaction.create(transaction),
      PaymentTransaction.create(payment),
      Bill.update({ ...bill.record, transaction: transaction.id })
    ], workflowStep.globalController);

    // Get the success state from the step state and update the values
    // This will show the success banner with the information set
    const { success } = workflowStep.getStepState;
  
    success.setSuccessState({ "open" : true, "text" : `Your payment for ${ bill.name } has been successfully scheduled.` });
    // Close the modal
    close("MakeAPaymentModal");
  };

  return (
    <>
      <CustomModalContent>
        <FormContainer>
          <FormRowView>
            <p className="form-extra-text">Please review the following payment.</p>
          </FormRowView>
          <FormRowView text="Pay From">
            <b>{ account.nickname } { account.number }(Available { account.availableBalance.format() })</b>
          </FormRowView>
          <FormRowView text="Pay To">
            <b>{ bill.name }</b>
          </FormRowView>

          { isEBill &&
            <>
              <FormRowView text="Apply Payment To">
                <b>{ applyPaymentTo }</b>
              </FormRowView>
              { showPaymentOption &&
                <FormRowView text="Payment Option">
                  <b>{ paymentOption }</b>
                </FormRowView> }
            </> }

          { showAmount &&
            <FormRowView text="Amount">
              <b>{ transaction.amount.format() }</b>
            </FormRowView> }

          <FormRowView text="Deliver By">
            <b>{ transaction.date.format("m/d/Y") }</b>
          </FormRowView>
        </FormContainer>
      </CustomModalContent>
      <CustomModalFooter>
        <ModalSecondaryButton text="Back" onClick={ backBtn } />
        <ModalPrimaryButton text="Pay" onClick={ submit } />
      </CustomModalFooter>
    </>
  );
}